import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./api";

export type Profile = {
  user_id: number;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  telegram_id: number | null;
  telegram_username: string | null;
  has_coach: boolean;
  coach: {
    id: number;
    name: string;
    /** Phase K3 — coach announced an away window. ISO date or null. */
    away_until?: string | null;
    away_message?: string | null;
    is_currently_away?: boolean;
  } | null;
  intervals_athlete_id: string | null;
  has_intervals_token: boolean;
  intervals_available: boolean;
  has_strava_token?: boolean;
  strava_available?: boolean;
  strava_athlete_id?: number | null;
  status: string;
  /** Account soft-pause (J-19). Reversible; distinct from scheduled deletion. */
  is_paused?: boolean;
  paused_until?: string | null;
  avatar_url?: string | null;
  /** Язык серверного контента (бриф/письма/TG) — сверяется с локалью UI. */
  language?: string | null;
  // WAVE 2.4 — Settings v2
  ftp_watts?: number | null;
  weight_kg?: number | null;
  primary_sport?: string | null;
  max_hr?: number | null;
  privacy?: PrivacySettings;
};

export type ProfileVisibility = "only_me" | "team" | "all";

export type PrivacySettings = {
  hide_locations: boolean;
  hide_weight: boolean;
  hide_training_plan: boolean;
  profile_visibility?: ProfileVisibility; // always sent by backend; optional so legacy fixtures compile
};

export type PrivacyPatch = Partial<PrivacySettings>;

export function usePatchPrivacy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: PrivacyPatch) => {
      const r = await api.patch<PrivacySettings>("/athlete/profile/privacy", patch);
      return r.data;
    },
    onSuccess: (next) => {
      qc.setQueryData<Profile>(["profile"], (prev) => {
        if (!prev) return prev;
        return { ...prev, privacy: next };
      });
    },
  });
}

export function usePauseAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (paused_until?: string | null) => {
      const r = await api.post("/athlete/profile/pause", { paused_until: paused_until || null });
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["profile"] }),
  });
}

export function useUnpauseAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => (await api.post("/athlete/profile/unpause")).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["profile"] }),
  });
}

export function useProfile() {
  return useQuery<Profile>({
    queryKey: ["profile"],
    queryFn: async () => (await api.get<Profile>("/athlete/profile")).data,
    refetchInterval: 10_000,
  });
}

export function useLinkTelegram() {
  return useMutation({
    mutationFn: async () => {
      const r = await api.post<{ deeplink: string; token: string; expires_in_seconds: number }>(
        "/athlete/profile/link-telegram"
      );
      return r.data;
    },
  });
}

export function useRedeemInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (code: string) => {
      const r = await api.post<Profile>("/athlete/profile/redeem-invite", { code });
      return r.data;
    },
    onSuccess: (data) => {
      qc.setQueryData(["profile"], data);
    },
  });
}


// ============================================================
// Settings (report schedule, morning message, notifications)
// ============================================================

export type AthleteSettings = {
  report_frequency: "weekly" | "biweekly" | "monthly" | "off";
  report_day: number; // 0=Mon..6=Sun
  report_time: string; // "HH:MM"
  report_locked_by_coach: boolean;
  morning_message_time: string | null; // "HH:MM" or null
  notification_preferences: Record<string, unknown>;
  hr_sensor: string | null;
};

export type AthleteSettingsPatch = {
  report_frequency?: AthleteSettings["report_frequency"];
  report_day?: number;
  report_time?: string;
  morning_message_time?: string; // "" to clear
  notification_preferences?: Record<string, unknown>;
  hr_sensor?: string;
};

export function useAthleteSettings() {
  return useQuery<AthleteSettings>({
    queryKey: ["athlete-settings"],
    queryFn: async () => (await api.get<AthleteSettings>("/athlete/profile/settings")).data,
  });
}

export function usePatchAthleteSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: AthleteSettingsPatch) => {
      const r = await api.patch<AthleteSettings>("/athlete/profile/settings", patch);
      return r.data;
    },
    onSuccess: (data) => {
      qc.setQueryData(["athlete-settings"], data);
    },
  });
}

// Персистит выбранный в UI язык в backend (User.language), чтобы серверный
// контент — daily brief, письма, уведомления — шёл на языке интерфейса, а не
// на языке регистрации. Вызывается только из authed-контекста (Settings).
export function useUpdateLanguage() {
  return useMutation({
    mutationFn: async (language: string) => {
      const r = await api.patch<{ language: string }>(
        "/athlete/profile/language",
        { language },
      );
      return r.data;
    },
  });
}


// ============================================================
// Quiet hours (silent notification window) + granular notification
// preferences. Backend: api/routers/athlete_profile.py:1310-1388.
// ============================================================

export type QuietHours = {
  // Час 0-23 в ЛОКАЛЬНОМ поясе пользователя, NULL = тихие часы выключены.
  // Здесь стояло «UTC hour» — та же неправда, что и в подписи полей на
  // экране: окно всегда считалось локально (`shared/services/quiet_hours`).
  start: number | null;
  end: number | null;
};

/** Ответ сервера — те же часы плюс пояс, в котором они считаются. */
export type QuietHoursState = QuietHours & { timezone?: string };

export function useQuietHours() {
  return useQuery<QuietHoursState>({
    queryKey: ["quiet-hours"],
    queryFn: async () =>
      (await api.get<QuietHoursState>("/athlete/profile/quiet-hours")).data,
  });
}

export function usePatchQuietHours() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: QuietHours) => {
      const r = await api.patch<QuietHours>("/athlete/profile/quiet-hours", body);
      return r.data;
    },
    onSuccess: (data) => {
      qc.setQueryData(["quiet-hours"], data);
    },
  });
}

/**
 * Six engagement notification toggles. Defaults all True (opt-out). Keys
 * mirror shared.services.notification_prefs_schema.ATHLETE_NOTIFICATION_PREFS.
 * Coach messages are NOT here — they're not optional by design.
 */
export type NotificationPrefs = {
  achievability_changes: boolean;
  race_week_daily: boolean;
  inactivity_nudge: boolean;
  new_race_recommendations: boolean;
  post_race_followup: boolean;
  morning_message: boolean;
};

type PrefsEnvelope = { prefs: NotificationPrefs };

export function useNotificationPrefs() {
  return useQuery<NotificationPrefs>({
    queryKey: ["notification-prefs"],
    queryFn: async () => {
      const r = await api.get<PrefsEnvelope>("/athlete/profile/notification-preferences");
      return r.data.prefs;
    },
  });
}

export function usePatchNotificationPrefs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: Partial<NotificationPrefs>) => {
      const r = await api.patch<PrefsEnvelope>("/athlete/profile/notification-preferences", {
        prefs: patch,
      });
      return r.data.prefs;
    },
    onSuccess: (data) => {
      qc.setQueryData(["notification-prefs"], data);
    },
  });
}


// ============================================================
// Intervals.icu sync
// ============================================================

export type SyncResult = {
  ok: boolean;
  stats: Record<string, number> | null;
  error: string | null;
  last_sync_at: string | null;
};

export type SyncIfStaleResult = {
  started: boolean;
  last_sync_at: string | null;
  age_seconds: number | null;
};

export type LastSyncInfo = {
  last_sync_at: string | null;
  age_seconds: number | null;
};

export function useLastIntervalsSync(enabled: boolean = true) {
  return useQuery<LastSyncInfo>({
    queryKey: ["intervals-last-sync"],
    queryFn: async () => (await api.get<LastSyncInfo>("/athlete/profile/intervals/last-sync")).data,
    enabled,
    refetchInterval: 30_000,
  });
}

export function useIntervalsSyncNow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const r = await api.post<SyncResult>("/athlete/profile/intervals/sync");
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["intervals-last-sync"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      qc.invalidateQueries({ queryKey: ["plan"] });
      qc.invalidateQueries({ queryKey: ["wellness-list"] });
    },
  });
}

export function useIntervalsSyncIfStale() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (maxAgeHours: number = 3) => {
      const r = await api.post<SyncIfStaleResult>(
        `/athlete/profile/intervals/sync-if-stale?max_age_hours=${maxAgeHours}`
      );
      return r.data;
    },
    onSuccess: (data) => {
      if (data.started) {
        // background sync started — invalidate after a few seconds
        setTimeout(() => {
          qc.invalidateQueries({ queryKey: ["intervals-last-sync"] });
          qc.invalidateQueries({ queryKey: ["dashboard"] });
          qc.invalidateQueries({ queryKey: ["plan"] });
        }, 8000);
      }
    },
  });
}


// ============================================================
// Strava sync (Sprint 38)
// ============================================================

export type StravaStatus = {
  connected: boolean;
  strava_athlete_id: number | null;
  last_sync_at: string | null;
  age_seconds: number | null;
};

export function useStravaStatus(enabled: boolean = true) {
  return useQuery<StravaStatus>({
    queryKey: ["strava-status"],
    queryFn: async () => (await api.get<StravaStatus>("/athlete/profile/strava/status")).data,
    enabled,
    refetchInterval: 30_000,
  });
}

export function useStravaSyncNow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const r = await api.post<SyncResult>("/athlete/profile/strava/sync");
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["strava-status"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      qc.invalidateQueries({ queryKey: ["plan"] });
    },
  });
}

export function useStravaSyncIfStale() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (maxAgeHours: number = 3) => {
      const r = await api.post<SyncIfStaleResult>(
        `/athlete/profile/strava/sync-if-stale?max_age_hours=${maxAgeHours}`
      );
      return r.data;
    },
    onSuccess: (data) => {
      if (data.started) {
        setTimeout(() => {
          qc.invalidateQueries({ queryKey: ["strava-status"] });
          qc.invalidateQueries({ queryKey: ["dashboard"] });
          qc.invalidateQueries({ queryKey: ["plan"] });
        }, 8000);
      }
    },
  });
}

export function useStravaDisconnect() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const r = await api.post("/athlete/profile/strava/disconnect");
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["strava-status"] });
      qc.invalidateQueries({ queryKey: ["profile"] });
    },
  });
}

// Revoke intervals.icu OAuth grant on our side (DELETE /athlete/profile/intervals,
// 204). Keeps imported workouts; the athlete purges those via DSAR delete.
export function useIntervalsDisconnect() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await api.delete("/athlete/profile/intervals");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["intervals-last-sync"] });
      qc.invalidateQueries({ queryKey: ["profile"] });
    },
  });
}

// ============================================================
// Data coverage — /athlete/profile/data-coverage (DataCoverageOut).
// Feeds the privacy dashboard's "what the model sees" readout.
// ============================================================

export type DataCoverage = {
  workouts_30d: number;
  sleep_14d: number;
  hrv_14d: number;
  past_races: number;
  intervals_connected: boolean;
  strava_connected: boolean;
  factors_used_estimate: number;
  factors_total: number;
};

export function useDataCoverage() {
  return useQuery<DataCoverage>({
    queryKey: ["athlete-data-coverage"],
    queryFn: async () =>
      (await api.get<DataCoverage>("/athlete/profile/data-coverage")).data,
  });
}


// Sprint 33 F8 — geolocation update.
export function useUpdateCoords() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: { lat: number; lon: number }) => {
      const r = await api.post("/athlete/profile/coords", params);
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["events"] });
      qc.invalidateQueries({ queryKey: ["profile"] });
    },
  });
}

// B4 — recording-device registry (populated from intervals.icu device_name).
export type AthleteDevice = {
  source: string;
  model: string;
  first_seen: string;
  last_seen: string;
  battery_pct: number | null;
};

export function useDevices() {
  return useQuery<AthleteDevice[]>({
    queryKey: ["athlete-devices"],
    queryFn: async () => (await api.get<AthleteDevice[]>("/athlete/devices")).data,
    staleTime: 5 * 60_000,
  });
}
